import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Typography,
} from '@mui/material';
import { authService } from '../../services/authService';
import { getHomeRoute } from '../../config/roles';
import { env } from '../../config/env';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import PasswordField from '../../components/form/PasswordField';

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const methods = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSignIn = async ({ email, password }) => {
    setError('');
    setLoading(true);
    try {
      const result = await authService.login(email, password);
      navigate(getHomeRoute(result.user.role));
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        backgroundColor: (t) => t.piper?.appBg ?? '#f5f6f8',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {env.appTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to continue
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

          <FormProvider methods={methods} onSubmit={onSignIn} autoComplete="off">
            <TextField
              name="email"
              label="Email"
              type="email"
              disabled={loading}
              autoComplete="username"
              sx={{ mb: 2 }}
            />
            <PasswordField
              name="password"
              label="Password"
              disabled={loading}
              autoComplete="current-password"
              sx={{ mb: 2.5 }}
            />
            <Button type="submit" fullWidth variant="contained" disabled={loading}>
              {loading ? (
                <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Signing in…</>
              ) : (
                'Sign in'
              )}
            </Button>
          </FormProvider>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
